'use client';

/**
 * LocationMapSection — a large, prominent map for the main column of a campsite
 * page (competitors lead with a big map, not a sidebar thumbnail). Map/Satellite
 * toggle, address line, and directions. Uses the Google Maps Embed API (no JS
 * SDK / quota beyond embeds).
 *
 * Tap to load. The embed iframe pulls its own copy of the Google Maps API
 * (~12 scripts) and was loading on every campsite page view, even for the
 * visitors who never look at the map (trace, 5 Oct 2026). The section now
 * shows a placeholder of the same size with a "Show map" button, and the
 * iframe only mounts when someone asks for it. The directions and
 * open-in-Maps links below work without it.
 */
import { withBase } from '../lib/brand';
import React, { useEffect, useState } from 'react';
import { useCaPreview } from '../lib/caPreview';
import { FaMapMarkerAlt, FaDirections, FaExternalLinkAlt, FaCompass } from 'react-icons/fa';
import PlaceMapTile from './store/PlaceMapTile';

const EMBED_KEY = 'AIzaSyAd6J-M2idmQ_dBi3tfD8CGPws_cpYvDEM';

interface Props {
  name: string;
  street?: string;
  street2?: string;
  town?: string;
  state?: string;
  postcode?: string;
  lat?: number | string;
  lng?: number | string;
}

export default function LocationMapSection({ name, street, street2, town, state, postcode, lat, lng }: Props) {
  const addressString = [street, street2, town, state, postcode].filter(Boolean).join(', ');
  const hasGps = lat !== undefined && lat !== null && lat !== '' && lng !== undefined && lng !== null && lng !== '';
  const query = hasGps ? `${lat},${lng}` : (addressString || name);

  // Clean roadmap view (satellite tiles read as cluttered for a single pin).
  const embedSrc =
    `https://www.google.com/maps/embed/v1/place?key=${EMBED_KEY}` +
    `&q=${encodeURIComponent(query)}&zoom=12&maptype=roadmap`;
  const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressString || name)}`;
  const openHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  // Our own Campsite Explorer map, centred on this spot (shows what's nearby too).
  const exploreHref =
    withBase(`?location=${encodeURIComponent([name, town, state].filter(Boolean).join(', '))}`);
  const [showMap, setShowMap] = useState(false);
  // ca_preview: the store never shows placeholder blocks. The box holds a
  // real map picture of the spot from our own tile server (an <img>, so it
  // is there as soon as it is scrolled to), and the interactive Google map
  // replaces it only when someone asks for it.
  const preview = useCaPreview();
  const latN = hasGps ? Number(lat) : NaN;
  const lngN = hasGps ? Number(lng) : NaN;
  const staticOk = preview && Number.isFinite(latN) && Number.isFinite(lngN);
  useEffect(() => {
    // No coordinates to draw a picture from: mount the embed as before.
    if (preview && !staticOk && !showMap) setShowMap(true);
  }, [preview, staticOk, showMap]);
  const placeLine = [town, state].filter(Boolean).join(', ');

  return (
    <section id="map-section" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="p-5 lg:px-8 lg:pt-7">
        <h2 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <FaMapMarkerAlt className="text-red-500" />
          Location
        </h2>
      </div>

      <div className="relative h-[340px] w-full bg-slate-100 md:h-[440px]">
        {staticOk && !showMap ? (
          <button
            type="button"
            onClick={() => setShowMap(true)}
            className="ca-locmap group absolute inset-0 block h-full w-full p-0"
            aria-label={`Open the interactive map for ${name}`}
          >
            <PlaceMapTile lat={latN} lng={lngN} width={820} height={440} zoom={11} className="absolute inset-0" eager />
            <span className="ca-locmap__cta">Interactive map</span>
          </button>
        ) : showMap ? (
          <iframe
            className="absolute inset-0 h-full w-full border-0"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={embedSrc}
            title={`Map of ${name}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowMap(true)}
            className="group absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-3 text-slate-700"
            aria-label={`Show the map for ${name}`}
            style={{
              // A quiet street-grid pattern so the box reads as a map before
              // it is one, with no image request.
              backgroundColor: '#eef2f5',
              backgroundImage:
                'linear-gradient(rgba(148,163,184,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.18) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          >
            <FaMapMarkerAlt className="text-4xl text-red-500 drop-shadow" aria-hidden="true" />
            <span className="max-w-[80%] text-center text-sm font-medium text-slate-800">
              {name}
              {placeLine && <span className="block text-xs font-normal text-slate-500">{placeLine}</span>}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#1558b0] shadow-md ring-1 ring-slate-200 transition group-hover:bg-[#e3f2fd]">
              <FaMapMarkerAlt aria-hidden="true" /> Show map
            </span>
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between lg:px-8 lg:pb-7">
        {addressString && (
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-800">{name}</span>
            <span className="mx-1.5 text-slate-300">•</span>
            {addressString}
          </p>
        )}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <a
            href={exploreHref}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            <FaCompass /> Explore on our map
          </a>
          <a
            href={directionsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-[#1558b0] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#1558b0]"
          >
            <FaDirections /> Get Directions
          </a>
          <a
            href={openHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Open in Maps <FaExternalLinkAlt className="text-xs" />
          </a>
        </div>
      </div>
    </section>
  );
}
